import './src/global.scss';

import React from 'react';
import { navigate } from 'gatsby';
import { GlobalLocalizationProvider, PageLocalizationProvider } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { LocationProvider } from '@reach/router';
import {
    ServicesContextProvider,
    MagentoStoreProvider,
    MagentoCustomerProvider,
    MagentoAPIProvider,
} from '@sbi-international/boutik.gatsby.plugin.services';
import { FeedbackProvider } from './src/contexts/feedback/FeedbackProvider';
import { FeedbackProviderWrapper } from './src/contexts/feedback/FeedbackProviderWrapper';
import { CartContextProvider } from '@sbi-international/boutik.gatsby.plugin.checkout';
import { cookieConsentConfig } from './cookieConsentConfig';
import { CookiesConsentProvider } from '@sbi-international/boutik.package.cookie.consent';

let visualEditingNavigate = null;

export const onClientEntry = () => {
    // Mounting the visual editing overlay creates a second React root on top
    // of the app — only do it when the Sanity draft overlay is enabled, like
    // <VisualEditing /> in Layout.tsx.
    if (process.env.GATSBY_SANITY_OVERLAY_DRAFTS !== 'true') return;

    import('@sanity/visual-editing').then(({ enableVisualEditing }) => {
        enableVisualEditing({
            zIndex: 9999,
            history: {
                subscribe: (nav) => {
                    visualEditingNavigate = nav;
                    return () => {
                        visualEditingNavigate = null;
                    };
                },
                update: ({ url }) => {
                    navigate(url);
                },
            },
        });
    });
};

export const onRouteUpdate = ({ location }) => {
    if (visualEditingNavigate) {
        visualEditingNavigate({ type: 'push', url: location.href });
    }
};

export const wrapRootElement = ({ element }, { magentoBackend }) => {
    const isBrowser = typeof window !== 'undefined';
    const apiBase = typeof window !== 'undefined' ? window.location?.origin : null;

    return (
        <GlobalLocalizationProvider>
            <LocationProvider>
                <FeedbackProvider>
                    <MagentoAPIProvider magentoUrl={apiBase || magentoBackend}>
                        <ServicesContextProvider magentoUrl={apiBase || magentoBackend}>
                            <FeedbackProviderWrapper>
                                {isBrowser ? (
                                    <CookiesConsentProvider config={cookieConsentConfig}>{element}</CookiesConsentProvider>
                                ) : (
                                    element
                                )}
                            </FeedbackProviderWrapper>
                        </ServicesContextProvider>
                    </MagentoAPIProvider>
                </FeedbackProvider>
            </LocationProvider>
        </GlobalLocalizationProvider>
    );
};

export const wrapPageElement = ({ element, props: { pageContext } }) => {
    return (
        <PageLocalizationProvider pageContext={pageContext}>
            {/* Keying on locale forces this subtree to remount on any locale change, so
                MagentoCustomerProvider/CartContextProvider re-read the new locale's storage
                keys instead of keeping the previous locale's cart/customer state (US18251). */}
            <MagentoStoreProvider key={pageContext.locale}>
                <MagentoCustomerProvider>
                    <CartContextProvider>{element}</CartContextProvider>
                </MagentoCustomerProvider>
            </MagentoStoreProvider>
        </PageLocalizationProvider>
    );
};
